import React from 'react'
import { Box, Grid, List, Typography } from '@mui/material'
import { useSessionContext } from 'session'
import { type ExecutivePosition, type ExecutiveStafferRole } from 'services/person/us/edit/executiveRolesService'
import { type ExecOrg } from 'services/executives/executiveOrgLookupService'
import { RoleItem } from '../legislative-roles/role-item'
import { RoleSection } from '../role-section/role-section'

interface Props {
  executiveRoles: ExecutivePosition[] | null
  stafferRoles: ExecutiveStafferRole[] | null
}

const formatTermRange = (startDate: string | null, endDate: string | null): string => {
  if (startDate === null && endDate === null) return ''
  if (startDate === null) return `until ${endDate ?? ''}`
  if (endDate === null) return `since ${startDate}`
  return `${startDate} – ${endDate}`
}

const formatOrgWithTitle = (org: ExecOrg, title: string): string => {
  const segments: string[] = []
  if (org.parentOrg?.name != null && org.parentOrg.name !== '') {
    segments.push(org.parentOrg.name)
  }
  segments.push(org.name)
  segments.push(title)
  return segments.join(' - ')
}

const formatPositionSecondary = (position: ExecutivePosition): string => {
  const termRange = formatTermRange(position.apptStartDate, position.apptEndDate)
  const parts = [position.execRole.descShort]
  if (termRange !== '') parts.push(termRange)
  return parts.join(' · ')
}

const formatStafferSecondary = (role: ExecutiveStafferRole): string =>
  `${role.execRole.descShort} · Staff to ${role.position.title}`

export const PersonExecutiveRolesBlock: React.FC<Props> = ({ executiveRoles, stafferRoles }) => {
  const { openNewTab } = useSessionContext()

  const handleRedirect = (executivePositionId: number): void => {
    openNewTab(`/us/executives/position-update/${executivePositionId}`)
  }

  const sections: Array<{ id: string, title: string, items: React.ReactNode[] }> = []

  if (executiveRoles != null && executiveRoles.length > 0) {
    sections.push({
      id: 'executive_positions',
      title: 'Executive Positions',
      items: executiveRoles.map((position) => (
        <RoleItem
          key={position.id}
          id={position.id}
          primary={formatOrgWithTitle(position.org, position.title)}
          secondary={formatPositionSecondary(position)}
          redirectHandler={() => { handleRedirect(position.id) }}
        />
      ))
    })
  }

  if (stafferRoles != null && stafferRoles.length > 0) {
    sections.push({
      id: 'executive_staff_roles',
      title: 'Executive Staff Roles',
      items: stafferRoles.map((role) => (
        <RoleItem
          key={role.id}
          id={role.id}
          primary={formatOrgWithTitle(role.position.org, role.title)}
          secondary={formatStafferSecondary(role)}
          redirectHandler={() => { handleRedirect(role.position.id) }}
        />
      ))
    })
  }

  return (
    <RoleSection
      title="Executive Branch Roles"
      isLoading={executiveRoles === null || stafferRoles === null}
      isEmpty={sections.length === 0}
      loadingMessage="Loading executive roles data..."
      emptyMessage="No executive roles found for this person."
    >
      <Grid container spacing={2}>
        {sections.map((section, index) => (
          <React.Fragment key={section.id}>
            <Grid item xs={12}>
              <Box mb={index < sections.length - 1 ? 2 : 0}>
                <Typography variant="subtitle1" gutterBottom>{section.title}</Typography>
                <List>{section.items}</List>
              </Box>
            </Grid>
          </React.Fragment>
        ))}
      </Grid>
    </RoleSection>
  )
}
